<template>
    <DashboardLayout>
        <Head title="SLA Policies" />

        <div class="py-6">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                <!-- Header -->
                <div class="mb-6">
                    <div class="flex items-center justify-between">
                        <div>
                            <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">SLA Policies</h1>
                            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Define service level agreements for work orders based on type, category, service, and priority combinations</p>
                        </div>
                        <Link
                            :href="route('settings.sla-policies.create')"
                            class="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 focus:bg-blue-700 active:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition ease-in-out duration-150"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                            </svg>
                            Add SLA Policy
                        </Link>
                    </div>
                </div>

                <!-- Filters -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <!-- Search -->
                        <div>
                            <label for="search" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
                            <input
                                id="search"
                                v-model="filters.search"
                                type="text"
                                placeholder="Search by name..."
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                @input="debouncedSearch"
                            />
                        </div>

                        <!-- Active Filter -->
                        <div>
                            <label for="active" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                            <select
                                id="active"
                                v-model="filters.active"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                @change="search"
                            >
                                <option value="">All</option>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                        </div>

                        <!-- Reset Button -->
                        <div class="flex items-end">
                            <button
                                @click="resetFilters"
                                class="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                                Reset Filters
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Policies Table -->
                <div class="bg-white rounded-lg shadow overflow-hidden">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead class="bg-gray-50">
                            <tr>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Policy Name
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Criteria
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Response Time
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Resolution Time
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Work Orders
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200 dark:divide-gray-700">
                            <tr v-for="policy in policies.data" :key="policy.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td class="px-6 py-4">
                                    <div class="flex items-center">
                                        <div>
                                            <div class="text-sm font-medium text-gray-900">
                                                {{ policy.name }}
                                            </div>
                                            <div v-if="policy.description" class="text-sm text-gray-500 line-clamp-1">
                                                {{ policy.description }}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4">
                                    <div class="text-sm text-gray-900">
                                        <div v-if="policy.work_order_type" class="mb-1">
                                            <span class="font-medium">Type:</span> {{ formatEnumValue(policy.work_order_type) }}
                                        </div>
                                        <div v-if="policy.service_category" class="mb-1">
                                            <span class="font-medium">Category:</span> {{ policy.service_category.name }}
                                        </div>
                                        <div v-if="policy.service" class="mb-1">
                                            <span class="font-medium">Service:</span> {{ policy.service.name }}
                                        </div>
                                        <div v-if="policy.priority" class="mb-1">
                                            <span class="font-medium">Priority:</span> {{ formatEnumValue(policy.priority) }}
                                        </div>
                                        <div v-if="!policy.work_order_type && !policy.service_category && !policy.service && !policy.priority" class="text-gray-500 italic">
                                            All work orders (default)
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <span v-if="policy.response_time_hours">{{ policy.response_time_hours }} hrs</span>
                                    <span v-else class="text-gray-400">Not set</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <span v-if="policy.resolution_time_hours">{{ policy.resolution_time_hours }} hrs</span>
                                    <span v-else class="text-gray-400">Not set</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {{ policy.work_orders_count }} orders
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        :class="[
                                            'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                                            policy.is_active
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-gray-100 text-gray-800'
                                        ]"
                                    >
                                        {{ policy.is_active ? 'Active' : 'Inactive' }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div class="flex items-center justify-end space-x-2">
                                        <Link
                                            :href="route('settings.sla-policies.edit', policy.id)"
                                            class="text-blue-600 hover:text-blue-900"
                                        >
                                            Edit
                                        </Link>
                                        <button
                                            @click="toggleActive(policy)"
                                            class="text-gray-600 hover:text-gray-900"
                                        >
                                            {{ policy.is_active ? 'Deactivate' : 'Activate' }}
                                        </button>
                                        <button
                                            v-if="policy.work_orders_count === 0"
                                            @click="deletePolicy(policy)"
                                            class="text-red-600 hover:text-red-900"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="policies.data.length === 0">
                                <td colspan="7" class="px-6 py-12 text-center text-sm text-gray-500">
                                    No SLA policies found. Create your first policy to define service level agreements for work orders.
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <!-- Pagination -->
                    <div v-if="policies.data.length > 0" class="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                        <Pagination :links="policies.links" />
                    </div>
                </div>

                <!-- Info Box -->
                <div class="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div class="flex">
                        <div class="flex-shrink-0">
                            <svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                            </svg>
                        </div>
                        <div class="ml-3 flex-1">
                            <p class="text-sm text-blue-700">
                                <strong>How SLA policies work:</strong> When a work order is created, the system automatically selects the most specific matching policy based on the work order's type, category, service, and priority. Policies with more specific criteria take precedence over general ones.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </DashboardLayout>
</template>

<script setup>
import { ref } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import { debounce } from 'lodash';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import Pagination from '@/Components/Pagination.vue';
import { useToast } from '@/Composables/useToast';

const props = defineProps({
    policies: Object,
    filters: Object,
});

const toast = useToast();

const filters = ref({
    search: props.filters.search || '',
    active: props.filters.active || '',
});

const search = () => {
    router.get(route('settings.sla-policies.index'), filters.value, {
        preserveState: true,
        preserveScroll: true,
    });
};

const debouncedSearch = debounce(search, 300);

const resetFilters = () => {
    filters.value = {
        search: '',
        active: '',
    };
    search();
};

const formatEnumValue = (value) => {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
};

const toggleActive = (policy) => {
    router.post(
        route('settings.sla-policies.toggleActive', policy.id),
        {},
        {
            preserveScroll: true,
            onSuccess: () => {
                toast.success(`Policy ${policy.is_active ? 'deactivated' : 'activated'} successfully`);
            },
            onError: () => {
                toast.error('Failed to update policy status');
            },
        }
    );
};

const deletePolicy = (policy) => {
    if (!confirm(`Are you sure you want to delete "${policy.name}"? This action cannot be undone.`)) {
        return;
    }

    router.delete(route('settings.sla-policies.destroy', policy.id), {
        preserveScroll: true,
        onSuccess: () => {
            toast.success('Policy deleted successfully');
        },
        onError: (errors) => {
            toast.error(errors.error || 'Failed to delete policy');
        },
    });
};
</script>
